<template>
  <div class="doc-edit">
    <!-- Loading -->
    <template v-if="loading">
      <TopBarTitleComponent title="Cargando..." :showBackIcon="true" />
      <b-container fluid class="pb-4 px-lg-4 pt-4">
        <SkeletonLoadingCard />
      </b-container>
    </template>

    <!-- Editor (template / scratch) -->
    <template v-else-if="document && document.source !== 'upload'">
      <div class="doc-editor-header">
        <div class="doc-editor-header__left">
          <button class="doc-editor-header__back" @click="confirmBack">
            <i class="el-icon-arrow-left" />
          </button>
          <input
            class="doc-editor-header__title-input"
            v-model="form.name"
            placeholder="Nombre del documento"
          />
        </div>
        <div class="doc-editor-header__actions">
          <span class="doc-editor-header__status">{{
            readStatus(document.status)
          }}</span>
          <button
            class="btn md__btn-third-secondary mr-2"
            @click="previewDocument"
          >
            <i class="el-icon-view" /> Vista previa
          </button>
          <button
            class="btn md__btn-primary"
            :disabled="saving"
            @click="saveDocument"
          >
            <i class="el-icon-loading" v-if="saving" />
            <i class="el-icon-check" v-else />
            Guardar
          </button>
        </div>
      </div>

      <div class="doc-editor-body">
        <!-- Columna izquierda: Editor TipTap -->
        <div class="doc-editor-body__editor">
          <div class="doc-toolbar" v-if="editor">
            <button
              class="doc-toolbar__btn"
              :class="{ 'doc-toolbar__btn--active': editor.isActive('bold') }"
              @click="
                editor
                  .chain()
                  .focus()
                  .toggleBold()
                  .run()
              "
              title="Negrita"
            >
              <b>B</b>
            </button>
            <button
              class="doc-toolbar__btn"
              :class="{ 'doc-toolbar__btn--active': editor.isActive('italic') }"
              @click="
                editor
                  .chain()
                  .focus()
                  .toggleItalic()
                  .run()
              "
              title="Cursiva"
            >
              <i>I</i>
            </button>
            <button
              class="doc-toolbar__btn"
              :class="{
                'doc-toolbar__btn--active': editor.isActive('underline')
              }"
              @click="
                editor
                  .chain()
                  .focus()
                  .toggleUnderline()
                  .run()
              "
              title="Subrayado"
            >
              <u>U</u>
            </button>
            <span class="doc-toolbar__sep" />
            <button
              class="doc-toolbar__btn"
              :class="{
                'doc-toolbar__btn--active': editor.isActive('heading', {
                  level: 1
                })
              }"
              @click="
                editor
                  .chain()
                  .focus()
                  .toggleHeading({ level: 1 })
                  .run()
              "
              title="Título 1"
            >
              H1
            </button>
            <button
              class="doc-toolbar__btn"
              :class="{
                'doc-toolbar__btn--active': editor.isActive('heading', {
                  level: 2
                })
              }"
              @click="
                editor
                  .chain()
                  .focus()
                  .toggleHeading({ level: 2 })
                  .run()
              "
              title="Título 2"
            >
              H2
            </button>
            <span class="doc-toolbar__sep" />
            <button
              class="doc-toolbar__btn"
              :class="{
                'doc-toolbar__btn--active': editor.isActive('bulletList')
              }"
              @click="
                editor
                  .chain()
                  .focus()
                  .toggleBulletList()
                  .run()
              "
              title="Lista"
            >
              <i class="el-icon-s-operation" />
            </button>
            <button
              class="doc-toolbar__btn"
              :class="{
                'doc-toolbar__btn--active': editor.isActive('orderedList')
              }"
              @click="
                editor
                  .chain()
                  .focus()
                  .toggleOrderedList()
                  .run()
              "
              title="Lista numerada"
            >
              <i class="el-icon-s-order" />
            </button>
            <span class="doc-toolbar__sep" />
            <button class="doc-toolbar__btn" @click="insertTable" title="Tabla">
              <i class="el-icon-s-grid" />
            </button>
            <button
              class="doc-toolbar__btn"
              @click="insertImage"
              title="Imagen"
            >
              <i class="el-icon-picture-outline" />
            </button>
            <span class="doc-toolbar__sep" />
            <button
              class="doc-toolbar__btn doc-toolbar__btn--signature"
              @click="insertSignatureZone"
              title="Zona de firma"
            >
              <i class="el-icon-edit" /> Firma
            </button>
          </div>
          <div class="doc-editor-content">
            <editor-content :editor="editor" />
          </div>
        </div>

        <!-- Columna derecha: Detalles -->
        <div class="doc-editor-body__sidebar">
          <div class="doc-sidebar-section">
            <p class="doc-sidebar-section__title">Detalles</p>
            <el-form label-position="top" size="small">
              <el-form-item label="Tipo de documento">
                <el-select v-model="form.type" placeholder="Seleccionar">
                  <el-option label="Contrato" value="contrato" />
                  <el-option label="NDA" value="nda" />
                  <el-option label="Propuesta" value="propuesta" />
                  <el-option label="Alcance" value="alcance" />
                  <el-option label="Otro" value="otro" />
                </el-select>
              </el-form-item>
              <el-form-item label="Expiración">
                <el-date-picker
                  v-model="form.expires_at"
                  type="date"
                  placeholder="Sin expiración"
                  format="dd/MM/yyyy"
                  value-format="yyyy-MM-dd"
                  style="width: 100%"
                />
              </el-form-item>
              <el-form-item label="Recordatorio">
                <el-select
                  v-model="form.reminder_frequency"
                  placeholder="Sin recordatorio"
                >
                  <el-option label="Sin recordatorio" :value="null" />
                  <el-option label="Cada 2 días" value="2_days" />
                  <el-option label="Semanal" value="weekly" />
                </el-select>
              </el-form-item>
            </el-form>
          </div>

          <div class="doc-sidebar-section">
            <p class="doc-sidebar-section__title">Zonas de firma</p>
            <p class="doc-sidebar-section__text" v-if="!signatureZones.length">
              Usa el botón "Firma" en la barra para insertar zonas.
            </p>
            <div v-else>
              <div
                class="doc-sidebar-sigzone"
                v-for="(zone, idx) in signatureZones"
                :key="idx"
              >
                <span>Firma {{ idx + 1 }}</span>
                <button
                  class="doc-sidebar-sigzone__remove"
                  @click="removeSignatureZone(idx)"
                >
                  <i class="el-icon-close" />
                </button>
              </div>
            </div>
          </div>

          <div class="doc-sidebar-section" v-if="document.quote">
            <p class="doc-sidebar-section__title">Cotización vinculada</p>
            <p class="doc-sidebar-section__text" style="color:#374151">
              #{{ document.quote.code }}
            </p>
          </div>
        </div>
      </div>

      <!-- Modal vista previa -->
      <el-dialog
        :visible.sync="previewVisible"
        fullscreen
        :show-close="false"
        class="doc-preview-dialog"
      >
        <div class="doc-preview-header">
          <p class="doc-preview-header__title">Vista previa</p>
          <button
            class="doc-preview-header__close"
            @click="previewVisible = false"
          >
            <i class="el-icon-close" />
          </button>
        </div>
        <div class="doc-preview-body" v-html="previewHtml" />
      </el-dialog>
    </template>

    <!-- Upload source: read-only view -->
    <template v-else-if="document && document.source === 'upload'">
      <TopBarTitleComponent :title="document.name" :showBackIcon="true">
        <template slot="right-area">
          <div class="d-flex align-items-center">
            <span class="doc-editor-header__status mr-3">{{
              readStatus(document.status)
            }}</span>
            <button
              class="btn md__btn-primary"
              :disabled="saving"
              @click="saveUploadMeta"
            >
              <i class="el-icon-loading" v-if="saving" />
              Guardar cambios
            </button>
          </div>
        </template>
      </TopBarTitleComponent>

      <b-container fluid class="pb-4 px-lg-4 pt-4">
        <b-row>
          <b-col md="8">
            <div class="doc-upload-preview">
              <div class="doc-upload-preview__placeholder">
                <i
                  class="el-icon-document"
                  style="font-size:48px;color:#9ca3af"
                />
                <p class="mt-2 text-muted">
                  Este documento fue cargado como PDF.<br />
                  Las zonas de firma se configuran en el paso de envío.
                </p>
              </div>
            </div>
          </b-col>
          <b-col md="4">
            <div class="doc-sidebar-section">
              <p class="doc-sidebar-section__title">Detalles</p>
              <el-form label-position="top" size="small">
                <el-form-item label="Nombre">
                  <el-input v-model="form.name" />
                </el-form-item>
                <el-form-item label="Tipo">
                  <el-select v-model="form.type" placeholder="Seleccionar">
                    <el-option label="Contrato" value="contrato" />
                    <el-option label="NDA" value="nda" />
                    <el-option label="Propuesta" value="propuesta" />
                    <el-option label="Alcance" value="alcance" />
                    <el-option label="Otro" value="otro" />
                  </el-select>
                </el-form-item>
                <el-form-item label="Expiración">
                  <el-date-picker
                    v-model="form.expires_at"
                    type="date"
                    placeholder="Sin expiración"
                    format="dd/MM/yyyy"
                    value-format="yyyy-MM-dd"
                    style="width: 100%"
                  />
                </el-form-item>
                <el-form-item label="Recordatorio">
                  <el-select
                    v-model="form.reminder_frequency"
                    placeholder="Sin recordatorio"
                  >
                    <el-option label="Sin recordatorio" :value="null" />
                    <el-option label="Cada 2 días" value="2_days" />
                    <el-option label="Semanal" value="weekly" />
                  </el-select>
                </el-form-item>
              </el-form>
            </div>
          </b-col>
        </b-row>
      </b-container>
    </template>
  </div>
</template>

<script>
import TopBarTitleComponent from "../../components/TopBarTitle";
import SkeletonLoadingCard from "@/components/Animations/Loading/SkeletonCard";
import documentoService from "../services/documentoService";

import { Editor, EditorContent } from "@tiptap/vue-2";
import StarterKit from "@tiptap/starter-kit";
import { Underline } from "@tiptap/extension-underline";
import { Image } from "@tiptap/extension-image";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { Placeholder } from "@tiptap/extension-placeholder";

export default {
  name: "DocumentEditPage",
  components: {
    TopBarTitleComponent,
    SkeletonLoadingCard,
    EditorContent
  },
  data() {
    return {
      loading: true,
      saving: false,
      document: null,
      editor: null,
      form: {
        name: "",
        type: "contrato",
        content: "",
        expires_at: null,
        reminder_frequency: null
      },
      signatureZones: [],
      previewVisible: false,
      previewHtml: ""
    };
  },
  methods: {
    readStatus(status) {
      const map = {
        draft: "Borrador",
        sent: "Enviado",
        viewed: "Visto",
        partially_signed: "Parcial",
        signed: "Firmado",
        expired: "Expirado",
        cancelled: "Cancelado"
      };
      return map[status] || status;
    },

    async loadDocument() {
      try {
        const response = await documentoService.getDocument(
          this.$route.params.id
        );
        const { success, data } = response.data;
        if (success) {
          this.document = data;
          this.form.name = data.name;
          this.form.type = data.type;
          this.form.content = data.content || "";
          this.form.expires_at = data.expires_at;
          this.form.reminder_frequency = data.reminder_frequency;
          this.signatureZones = data.signature_zones || [];

          if (data.source !== "upload") {
            this.initEditor(data.content || "");
          }
        } else {
          this.$notifications.error({ message: "Documento no encontrado" });
          this.$router.push({ name: "documentos" });
        }
      } catch (e) {
        this.$notifications.error({ message: "Error al cargar el documento" });
        this.$router.push({ name: "documentos" });
      } finally {
        this.loading = false;
      }
    },

    initEditor(content) {
      if (this.editor) this.editor.destroy();
      this.editor = new Editor({
        content: content || "",
        extensions: [
          StarterKit,
          Underline,
          Image,
          Table.configure({ resizable: true }),
          TableRow,
          TableCell,
          TableHeader,
          Placeholder.configure({
            placeholder: "Escribe el contenido de tu documento aquí..."
          })
        ],
        onUpdate: ({ editor }) => {
          this.form.content = editor.getHTML();
        }
      });
    },

    insertTable() {
      this.editor
        .chain()
        .focus()
        .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
        .run();
    },

    insertImage() {
      const url = prompt("URL de la imagen:");
      if (url) {
        this.editor
          .chain()
          .focus()
          .setImage({ src: url })
          .run();
      }
    },

    insertSignatureZone() {
      const idx = this.signatureZones.length + 1;
      this.signatureZones.push({
        label: `Firmante ${idx}`,
        x: 10,
        y: 80,
        width: 30,
        height: 8,
        page: 1
      });
      this.editor
        .chain()
        .focus()
        .insertContent(
          `<p style="border:2px dashed #3ecfb4;padding:12px;border-radius:8px;text-align:center;color:#3ecfb4;font-weight:600;">` +
            `[Zona de firma ${idx}]</p>`
        )
        .run();
    },

    removeSignatureZone(idx) {
      this.signatureZones.splice(idx, 1);
    },

    async saveDocument() {
      if (!this.form.name) {
        this.$notifications.warning({ message: "Ingresa un nombre" });
        return;
      }
      this.saving = true;
      try {
        const payload = {
          name: this.form.name,
          type: this.form.type,
          content: this.form.content,
          expires_at: this.form.expires_at,
          reminder_frequency: this.form.reminder_frequency,
          signature_zones: this.signatureZones
        };
        const response = await documentoService.updateDocument(
          this.document.id,
          payload
        );
        const { success } = response.data;
        if (success) {
          this.$notifications.success({ message: "Documento actualizado" });
        } else {
          this.$notifications.error({
            message: response.data.message || "Error al guardar"
          });
        }
      } catch (e) {
        this.$notifications.error({ message: "Error al guardar" });
      } finally {
        this.saving = false;
      }
    },

    async saveUploadMeta() {
      this.saving = true;
      try {
        const payload = {
          name: this.form.name,
          type: this.form.type,
          expires_at: this.form.expires_at,
          reminder_frequency: this.form.reminder_frequency
        };
        const response = await documentoService.updateDocument(
          this.document.id,
          payload
        );
        const { success } = response.data;
        if (success) {
          this.$notifications.success({ message: "Documento actualizado" });
        } else {
          this.$notifications.error({
            message: response.data.message || "Error al guardar"
          });
        }
      } catch (e) {
        this.$notifications.error({ message: "Error al guardar" });
      } finally {
        this.saving = false;
      }
    },

    previewDocument() {
      this.previewHtml = this.form.content;
      this.previewVisible = true;
    },

    confirmBack() {
      this.$confirm(
        "¿Salir sin guardar? Los cambios no guardados se perderán.",
        "Confirmar",
        {
          confirmButtonText: "Salir",
          cancelButtonText: "Cancelar",
          type: "warning"
        }
      )
        .then(() => {
          this.$router.push({
            name: "documentos.detail",
            params: { id: this.document.id }
          });
        })
        .catch(() => {});
    }
  },
  mounted() {
    this.loadDocument();
  },
  beforeDestroy() {
    if (this.editor) this.editor.destroy();
  }
};
</script>

<style lang="scss" scoped>
/* ─── Editor header ──────────────────────────────────────────────── */
.doc-editor-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
  background: #fff;
  border-bottom: 1px solid #e4eaf6;
  position: sticky;
  top: 0;
  z-index: 10;
}
.doc-editor-header__left {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}
.doc-editor-header__back {
  border: none;
  background: none;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 18px;
  color: #989aae;
  margin-right: 8px;
  &:hover {
    background: #e4eaf6;
  }
}
.doc-editor-header__title-input {
  border: none;
  outline: none;
  font-size: 16px;
  font-weight: 600;
  color: #31355c;
  flex: 1;
  min-width: 0;
  padding: 4px 8px;
  border-radius: 10px;
  &:focus {
    background: #fafbfd;
  }
  &::placeholder {
    color: #989aae;
  }
}
.doc-editor-header__actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
}
.doc-editor-header__status {
  font-size: 12px;
  font-weight: 600;
  color: #7e819f;
  background: #e4eaf6;
  padding: 4px 12px;
  border-radius: 20px;
}

/* ─── Editor body ────────────────────────────────────────────────── */
.doc-editor-body {
  display: flex;
  height: calc(100vh - 56px);
  overflow: hidden;
}
.doc-editor-body__editor {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  border-right: 1px solid #e4eaf6;
}
.doc-editor-body__sidebar {
  width: 280px;
  overflow-y: auto;
  padding: 20px;
  flex-shrink: 0;
  background: #f7f8f9;
}

/* ─── Toolbar ────────────────────────────────────────────────────── */
.doc-toolbar {
  display: flex;
  align-items: center;
  padding: 6px 12px;
  border-bottom: 1px solid #e4eaf6;
  background: #fff;
  flex-wrap: wrap;
  gap: 2px;
}
.doc-toolbar__btn {
  border: none;
  background: none;
  padding: 5px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  color: #989aae;
  transition: all 0.15s;
  &:hover {
    background: #e4eaf6;
    color: #31355c;
  }
  &--active {
    background: #e4eaf6;
    color: #31355c;
    font-weight: 600;
  }
  &--signature {
    color: #3fdbbf;
    font-weight: 500;
    &:hover {
      background: #e6faf5;
    }
  }
}
.doc-toolbar__sep {
  width: 1px;
  height: 20px;
  background: #e4eaf6;
  margin: 0 4px;
}

/* ─── Editor content ─────────────────────────────────────────────── */
.doc-editor-content {
  flex: 1;
  overflow-y: auto;
  padding: 32px;
  background: #f7f8f9;
}
.doc-editor-content ::v-deep .ProseMirror {
  background: #fff;
  border: 1px solid #e4eaf6;
  border-radius: 16px;
  padding: 40px;
  min-height: 600px;
  max-width: 800px;
  margin: 0 auto;
  box-shadow: 0 0 10px 0 #d6e4f3;
  outline: none;
  font-size: 14px;
  line-height: 1.7;
  color: #31355c;

  h1 {
    font-size: 24px;
    font-weight: 700;
    margin: 24px 0 12px;
  }
  h2 {
    font-size: 20px;
    font-weight: 600;
    margin: 20px 0 10px;
  }
  p {
    margin: 0 0 12px;
  }
  ul,
  ol {
    padding-left: 24px;
    margin: 0 0 12px;
  }
  table {
    border-collapse: collapse;
    width: 100%;
    margin: 16px 0;
    td,
    th {
      border: 1px solid #e4eaf6;
      padding: 8px 12px;
      font-size: 13px;
    }
    th {
      background: #f7f8f9;
      font-weight: 600;
    }
  }
  img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
  }
  p.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    float: left;
    color: #989aae;
    pointer-events: none;
    height: 0;
  }
}

/* ─── Sidebar ────────────────────────────────────────────────────── */
.doc-sidebar-section {
  margin-bottom: 24px;
  &__title {
    font-size: 12px;
    font-weight: 600;
    color: #989aae;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 0 0 10px;
  }
  &__text {
    font-size: 13px;
    color: #989aae;
    margin: 0;
  }
}
.doc-sidebar-sigzone {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  background: #fff;
  border: 1.5px solid #e4eaf6;
  border-radius: 10px;
  font-size: 13px;
  color: #31355c;
  margin-bottom: 6px;
  &__remove {
    border: none;
    background: none;
    color: #e24b4a;
    cursor: pointer;
    padding: 2px;
    font-size: 14px;
  }
}

/* ─── Upload preview ─────────────────────────────────────────────── */
.doc-upload-preview {
  background: #fff;
  border: 1.5px solid #e4eaf6;
  border-radius: 16px;
  padding: 60px;
  text-align: center;
  box-shadow: 0 0 10px 0 #d6e4f3;
}
.doc-upload-preview__placeholder p {
  font-size: 13px;
  line-height: 1.6;
}

/* ─── Preview dialog ─────────────────────────────────────────────── */
.doc-preview-dialog ::v-deep .el-dialog {
  margin: 0 !important;
  border-radius: 0;
}
.doc-preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-bottom: 1px solid #e4eaf6;
  background: #fff;
  &__title {
    font-size: 16px;
    font-weight: 600;
    color: #31355c;
    margin: 0;
  }
  &__close {
    border: none;
    background: #31355c;
    color: #fff;
    font-size: 16px;
    cursor: pointer;
    width: 34px;
    height: 34px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    &:hover {
      opacity: 0.85;
    }
  }
}
.doc-preview-body {
  padding: 40px;
  max-width: 800px;
  margin: 0 auto;
  font-size: 14px;
  line-height: 1.7;
  color: #31355c;
}

/* ─── Responsive ─────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .doc-editor-body {
    flex-direction: column;
    height: auto;
  }
  .doc-editor-body__sidebar {
    width: 100%;
    border-top: 1px solid #e4eaf6;
  }
  .doc-editor-body__editor {
    border-right: none;
  }
}
</style>
